import {
  PublicClientApplication,
  type Configuration,
  type AccountInfo,
  type AuthenticationResult,
  InteractionRequiredAuthError,
} from '@azure/msal-browser'
import config from '@/config/b2c'
import axios from 'axios'

const EMPTY_TOKEN_REDIRECT_KEY = 'arago.redirectionJetonVide'
const EMPTY_TOKEN_REDIRECT_WINDOW_MS = 2 * 60 * 1000

export class AuthService {
  private msalInstance: PublicClientApplication
  private applicationConfig: {
    clientID: string
    authority: string
    scopes: string[]
    reset: string
  }
  private initPromise: Promise<void>
  private isRedirecting: boolean = false // Flag pour tracker les redirects en cours
  
  constructor() {
    this.applicationConfig = {
      clientID: config.clientid,
      authority: config.signin,
      scopes: config.b2cScopes,
      reset: config.reset,
    }
    
    const msalConfig: Configuration = {
      auth: {
        clientId: this.applicationConfig.clientID,
        authority: this.applicationConfig.authority,
        knownAuthorities: [config.authorityDomain],
        redirectUri: window.location.origin + '/', // HTML5 history mode
        postLogoutRedirectUri: window.location.origin + '/',
        navigateToLoginRequestUrl: true, // Naviguer vers l'URL demandée après login
      },
      cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: true,
      },
    }
    
    this.msalInstance = new PublicClientApplication(msalConfig)
    
    // Initialiser MSAL et stocker la promesse
    this.initPromise = this.initialize()
  }
  
  private async initialize(): Promise<void> {
    await this.msalInstance.initialize()
    
    // Gérer les redirections
    try {
      await this.msalInstance.handleRedirectPromise()
    } catch (error: any) {
      if (error?.errorMessage?.includes('AADB2C90118')) {
        // Reset password flow
        this.isRedirecting = true
        await this.performPasswordReset()
        return // Bloquer l'initialisation
      } else if (error?.errorMessage?.includes('AADB2C90091')) {
        // User cancelled
        this.login()
      } else if (error) {
        console.error('Erreur MSAL redirect:', error)
        this.login()
      }
    }
  }
  
  // Méthode publique pour attendre l'initialisation
  async waitForInitialization(): Promise<void> {
    await this.initPromise
  }
  
  async login() {
    // Attendre l'initialisation avant d'appeler loginRedirect
    await this.initPromise
    
    this.msalInstance.loginRedirect({
      scopes: this.applicationConfig.scopes,
      authority: this.applicationConfig.authority,
    }).catch(error => {
      console.error('Erreur login:', error)
    })
  }
  
  async logout() {
    await this.initPromise
    
    const account = this.getAccount()
    if (account) {
      // Reset store sera fait dans le router
      this.msalInstance.logoutRedirect({
        account,
      }).catch(error => {
        console.error('Erreur logout:', error)
      })
    }
  }
  
  // Méthode interne pour le reset password (appelée depuis initialize())
  private async performPasswordReset(): Promise<never> {
    this.isRedirecting = true
    
    await this.msalInstance.loginRedirect({
      scopes: this.applicationConfig.scopes,
      authority: this.applicationConfig.reset,
    }).catch(error => {
      console.error('Erreur reset password:', error)
    })
    
    // Retourner une promesse qui ne se résout JAMAIS pour bloquer l'exécution
    // Le navigateur va rediriger vers B2C, donc ce code ne devrait jamais continuer
    return new Promise(() => {}) as never
  }
  
  // Méthode publique pour le reset password (API externe)
  async resetPassword(): Promise<never> {
    await this.initPromise
    return this.performPasswordReset()
  }

  async getToken(): Promise<AuthenticationResult | null> {
    await this.initPromise
    
    const account = this.getAccount()
    if (!account) {
      return null
    }
    
    const tokenRequest = {
      scopes: this.applicationConfig.scopes,
      account,
    }
    
    try {
      // Essayer d'acquérir le token silencieusement
      const result = await this.msalInstance.acquireTokenSilent(tokenRequest)
      if (result.accessToken) {
        return result
      }

      // Jeton d'accès vide (ex. réponse mise en cache pendant que B2C ne délivrait plus le scope de l'API) :
      // renouvellement forcé, puis reconnexion transparente plutôt qu'envoyer « Bearer » sans jeton
      const refreshed = await this.msalInstance.acquireTokenSilent({ ...tokenRequest, forceRefresh: true })
      if (refreshed.accessToken) {
        return refreshed
      }
      await this.redirectForEmptyAccessToken(tokenRequest)
      return null
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // Token expiré ou interaction requise - redirect (ne retourne rien)
        await this.msalInstance.acquireTokenRedirect(tokenRequest).catch(redirectError => {
          console.error('Erreur acquireTokenRedirect:', redirectError)
          this.logout()
        })
        return null
      } else {
        console.error('Erreur getToken:', error)
        return null
      }
    }
  }
  
  // Une seule reconnexion par fenêtre de 2 min : si B2C ne délivre toujours pas de jeton, pas de boucle de redirection
  private async redirectForEmptyAccessToken(tokenRequest: { scopes: string[]; account: AccountInfo }): Promise<void> {
    const lastRedirect = Number(sessionStorage.getItem(EMPTY_TOKEN_REDIRECT_KEY) ?? 0)
    if (Date.now() - lastRedirect < EMPTY_TOKEN_REDIRECT_WINDOW_MS) {
      console.error('Jeton d\'accès B2C toujours vide après reconnexion')
      return
    }
    sessionStorage.setItem(EMPTY_TOKEN_REDIRECT_KEY, String(Date.now()))
    await this.msalInstance.acquireTokenRedirect(tokenRequest).catch(redirectError => {
      console.error('Erreur acquireTokenRedirect:', redirectError)
    })
  }

  async getAxiosTokenConfig(): Promise<{ headers: { authorization: string } } | null> {
    const tokenResponse = await this.getToken()
    if (!tokenResponse) {
      return null
    }
    
    return {
      headers: {
        authorization: 'Bearer ' + tokenResponse.accessToken,
      },
    }
  }
  
  getAccount(): AccountInfo | null {
    // Vérifier si MSAL est initialisé avant d'appeler getAllAccounts
    // En MSAL v4, getAllAccounts() nécessite que initialize() soit terminé
    try {
      const accounts = this.msalInstance.getAllAccounts()
      if (accounts.length > 0) {
        return accounts[0]
      }
    } catch (error) {
      // Si MSAL n'est pas encore initialisé, retourner null
      console.debug('MSAL not initialized yet')
      return null
    }
    return null
  }
  
  isAuthenticated(): boolean {
    // Méthode synchrone pour compatibilité avec Vue computed
    return this.getAccount() !== null
  }
  
  isInB2CFlow(): boolean {
    // Détecte si nous sommes dans un flux B2C actif (login, password reset, etc.)
    // en vérifiant si l'URL contient des paramètres B2C ou si MSAL traite une interaction
    const url = window.location.href
    const hasB2CParams = url.includes('code=') || 
                         url.includes('error=') || 
                         url.includes('state=') ||
                         url.includes('id_token=') ||
                         url.includes('access_token=')
    
    return hasB2CParams || this.isRedirecting
  }
  
  // Méthodes de vérification des droits module
  private async checkModuleMembership(moduleName: string): Promise<boolean> {
    try {
      const config = await this.getAxiosTokenConfig()
      if (!config) {
        return false
      }
      
      const baseUrl = import.meta.env.VUE_APP_BASE_URL || process.env.VUE_APP_BASE_URL
      const account = this.getAccount()
      
      if (!account) {
        return false
      }
      
      const response = await axios({
        url: `${baseUrl}/api/arago/${moduleName}-member`,
        method: 'GET',
        params: {
          userID: account.localAccountId || account.homeAccountId,
        },
        headers: config.headers,
      })
      
      return response.data
    } catch (error) {
      console.error(`Erreur vérification ${moduleName}:`, error)
      return false
    }
  }
  
  async isSolvencyMember(): Promise<boolean> {
    return this.checkModuleMembership('solvency')
  }
  
  async isTransparencyMember(): Promise<boolean> {
    return this.checkModuleMembership('transparency')
  }
  
  async isSimulationMember(): Promise<boolean> {
    return this.checkModuleMembership('simulation')
  }
  
  async isEsgMember(): Promise<boolean> {
    return this.checkModuleMembership('esg')
  }
  
  async isRiskMember(): Promise<boolean> {
    return this.checkModuleMembership('risk')
  }
}

// Export singleton
let authServiceInstance: AuthService | null = null

export function getAuthService(): AuthService {
  if (!authServiceInstance) {
    authServiceInstance = new AuthService()
  }
  return authServiceInstance
}

export default AuthService
